import { createSlice } from '@reduxjs/toolkit';

import { TIMEKEEPING_STATUSES } from '../../constants/jobOrderConstants';

const updatePlacement = (jobOrders, placementId, status) =>
  jobOrders.map((job) => ({
    ...job,
    placements: (Array.isArray(job.placements) ? job.placements : []).map((pl) => {
      if (pl._id === placementId) {
        return {
          ...pl,
          timecard: {
            ...pl.timecard,
            status,
          },
        };
      }
      return pl;
    }),
  }));

const updatePlacementStatusFromDocuments = (jobOrders, updatedDocument, defaultStatus) =>
  jobOrders.map((job) => ({
    ...job,
    placements: (Array.isArray(job.placements) ? job.placements : []).map((placement) => {
      const timecards = updatedDocument?.timecards;
      const updatedDoc = Array.isArray(timecards)
        ? timecards.find((doc) => doc.placementId === placement._id)
        : undefined;

      if (updatedDoc) {
        return {
          ...placement,
          timecard: {
            ...placement.timecard,
            status: updatedDoc?.timecard?.status || defaultStatus,
          },
        };
      }
      return placement;
    }),
  }));

const initialState = {
  // List-only state (job detail will be split out separately)
  jobOrdersV2: [],
  cursorV2: null,
  hasMoreV2: false,
  isJobOrdersV2Loading: false,
  timecardUpdateErrorV2: null,
  timeCardIsUpdatingV2: false,
  timecardActionIsProcessingV2: false,
  isPostingJobRosterV2: false,
};

export const jobOrdersSlice = createSlice({
  name: 'jobOrdersV2Slice',
  initialState,
  reducers: {
    /* eslint-disable no-param-reassign */
    getJobOrdersV2: (state, action) => {
      const isFirstPage = action?.payload?.cursor == null;
      if (isFirstPage) {
        state.jobOrdersV2 = [];
        state.cursorV2 = null;
        state.hasMoreV2 = false;
      }
      state.isJobOrdersV2Loading = true;
    },
    getJobOrdersProcessedV2: (state, action) => {
      state.isJobOrdersV2Loading = false;
      const { cursor, documents, hasMore } = action?.payload?.data || {};
      const safeDocuments = Array.isArray(documents) ? documents : [];
      if (!Array.isArray(state.jobOrdersV2)) {
        state.jobOrdersV2 = [];
      }
      state.jobOrdersV2.push(...safeDocuments);
      state.hasMoreV2 = hasMore === true;
      state.cursorV2 = cursor ?? null;
    },
    getJobOrdersErrorV2: (state) => {
      state.isJobOrdersV2Loading = false;
      state.jobOrdersV2 = [];
      state.cursorV2 = null;
      state.hasMoreV2 = false;
    },

    clearTimeCardErrorV2: (state) => {
      state.timecardUpdateErrorV2 = null;
    },

    timecardEditV2ForJobOrderV2: (state) => {
      state.timeCardIsUpdatingV2 = true;
    },
    timecardEditV2ForJobOrderProcessedV2: (state, action) => {
      state.timeCardIsUpdatingV2 = false;
      const { timecards } = action?.payload?.data?.document || {};
      if (!Array.isArray(timecards) || !Array.isArray(state.jobOrdersV2)) {
        return;
      }

      state.jobOrdersV2 = state.jobOrdersV2.map((job) => ({
        ...job,
        placements: (Array.isArray(job.placements) ? job.placements : []).map((pl) => {
          if (timecards.some((time) => time.placementId === pl._id)) {
            const foundTimecard = timecards.find((time) => time.placementId === pl._id);
            const totalHours = foundTimecard?.timecard?.totalHours;
            const breakTime = foundTimecard?.timecard?.break;
            return {
              ...pl,
              timecard: {
                ...pl.timecard,
                current: {
                  ...pl.timecard.current,
                  punches: foundTimecard?.timecard?.punches,
                  totalTime: totalHours ? totalHours * 60 * 60 * 1000 : '--',
                  breakTime,
                },
              },
            };
          }
          return pl;
        }),
      }));
    },
    timecardEditV2ForJobOrderErrorV2: (state, action) => {
      state.timeCardIsUpdatingV2 = false;
      const messageData =
        action?.payload?.errors?.GLOBAL?.messageKey || 'error.generic.something.went.wrong';
      state.timecardUpdateErrorV2 = messageData;
    },

    updatePlacementCheckInStatusV2: (state) => {
      state.isPostingJobRosterV2 = true;
    },
    updatePlacementCheckInStatusProcessedV2: (state, action) => {
      state.isPostingJobRosterV2 = false;
      const document = action?.payload?.data?.document;

      state.jobOrdersV2 = state.jobOrdersV2.map((job) => {
        if (job._id === document?.jobOrder?._id) {
          return {
            ...job,
            placements: (Array.isArray(job.placements) ? job.placements : []).map((placement) => {
              if (placement._id === document?._id) {
                return {
                  ...placement,
                  timecard: {
                    ...placement.timecard,
                    checkIn: document?.timecard?.checkIn,
                  },
                };
              }
              return placement;
            }),
          };
        }
        return job;
      });
    },
    updatePlacementCheckInStatusErrorV2: (state) => {
      state.isPostingJobRosterV2 = false;
    },

    postPlacementApprovalV2: (state) => {
      state.timecardActionIsProcessingV2 = true;
    },
    postPlacementApprovalProcessedV2: (state, action) => {
      state.timecardActionIsProcessingV2 = false;
      const updatedDocument = action?.payload?.data?.updated?.documents?.[0];

      state.jobOrdersV2 = updatePlacementStatusFromDocuments(
        state.jobOrdersV2,
        updatedDocument,
        TIMEKEEPING_STATUSES.APPROVED
      );
    },
    postPlacementApprovalErrorV2: (state) => {
      state.timecardActionIsProcessingV2 = false;
    },

    postPlacementResolveV2: (state) => {
      state.timecardActionIsProcessingV2 = true;
    },
    postPlacementResolveProcessedV2: (state, action) => {
      state.timecardActionIsProcessingV2 = false;
      const updatedDocument = action?.payload?.data?.updated?.documents?.[0];

      state.jobOrdersV2 = updatePlacementStatusFromDocuments(
        state.jobOrdersV2,
        updatedDocument,
        TIMEKEEPING_STATUSES.RESOLVED
      );
    },
    postPlacementResolveErrorV2: (state) => {
      state.timecardActionIsProcessingV2 = false;
    },

    postPlacementFinalizeV2: (state) => {
      state.timecardActionIsProcessingV2 = true;
    },
    postPlacementFinalizeProcessedV2: (state, action) => {
      state.timecardActionIsProcessingV2 = false;
      const updatedDocument = action?.payload?.data?.updated?.documents?.[0];

      state.jobOrdersV2 = updatePlacementStatusFromDocuments(
        state.jobOrdersV2,
        updatedDocument,
        TIMEKEEPING_STATUSES.FINALIZED
      );
    },
    postPlacementFinalizeErrorV2: (state) => {
      state.timecardActionIsProcessingV2 = false;
    },

    postPlacementUnFinalizeV2: (state) => {
      state.timecardActionIsProcessingV2 = true;
    },
    postPlacementUnFinalizeProcessedV2: (state, action) => {
      state.timecardActionIsProcessingV2 = false;
      const placementId = action?.payload?.data?.placements?.[0];
      if (!placementId) {
        return;
      }
      state.jobOrdersV2 = updatePlacement(
        state.jobOrdersV2,
        placementId,
        TIMEKEEPING_STATUSES.RESOLVED
      );
    },
    postPlacementUnFinalizeErrorV2: (state) => {
      state.timecardActionIsProcessingV2 = false;
    },
  },
});

export const jobOrdersV2Reducer = jobOrdersSlice.reducer;

export const {
  getJobOrdersV2,
  getJobOrdersErrorV2,
  getJobOrdersProcessedV2,
  clearTimeCardErrorV2,
  timecardEditV2ForJobOrderV2,
  timecardEditV2ForJobOrderProcessedV2,
  timecardEditV2ForJobOrderErrorV2,
  updatePlacementCheckInStatusV2,
  updatePlacementCheckInStatusProcessedV2,
  updatePlacementCheckInStatusErrorV2,
  postPlacementApprovalV2,
  postPlacementApprovalProcessedV2,
  postPlacementApprovalErrorV2,
  postPlacementResolveV2,
  postPlacementResolveProcessedV2,
  postPlacementResolveErrorV2,
  postPlacementFinalizeV2,
  postPlacementFinalizeProcessedV2,
  postPlacementFinalizeErrorV2,
  postPlacementUnFinalizeV2,
  postPlacementUnFinalizeProcessedV2,
  postPlacementUnFinalizeErrorV2,
} = jobOrdersSlice.actions;
